import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import 'mobx-react-lite/batchingForReactDom';
// import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { useStores } from '../../../hooks';
import { SimulcastTable, Selection, getOptionTextByValue } from '../../../elements';
import 'bootstrap/dist/css/bootstrap.min.css';
import Schema, { prepareSchemaProfile } from './schema/';
import {
  messageTypeValuesWithAll as messageTypes,
  allMessageType,
} from '../../../data/messageTypes';

// FIXME: Select the lane in dropdown when clicking the View Transcript button from the lanes view.
const AuctionTranscriptReportHome = observer(
  ({ AuctionID, laneID, onAuctionLaneChange }) => {
    const {
      AuctionStore: {
        sortedActiveAuctions,
        getSortedActiveAuctions,
        loadAuctionLaneOptions,
        laneOptions,
      },
      AuctionTranscriptDataStore: {
        auctionTranscriptData: data,
        totalItems,
        isLoading,
        loadAuctionTranscriptData,
      },
      UIStore: { setHeaderText },
    } = useStores();


    const [isLoadingAuctions, setIsLoadingAuctions] = useState(true);
    const [isLoadingLanes, setIsLoadingLanes] = useState(false);
    const [isLoadingMessageTypes, setIsLoadingMessageTypes] = useState(true);

    const [auction, setAuction] = useState({
      key: AuctionID,
      value: AuctionID,
      text: 'Some Auction',
    });

    const [lane, setLane] = useState({
      key: laneID,
      value: laneID,
      text: 'All Lanes',
    });

    const [messageType, setMessageType] = useState(allMessageType);
    const [searchParams, setSearchParams] = useState([]);

    // =========== Table ===========

    const schema = Schema('auctionTranscript');
    const prepareData = prepareSchemaProfile(schema, {
      auctionId: auction?.value,
      laneID: lane?.value === 'ALL' ? null : lane?.value,
      messageType:
        messageType.value === allMessageType.value ? null : messageType.value,
      searchText: searchParams,
      debugging: false,
      fn: loadAuctionTranscriptData,
    });

    const tableKey = `${auction?.value}:${auction?.text}~${lane?.text}~${messageType?.text}`;

    const {
      bodySchema,
      debugging,
      headerSchema,
      methods,
      pageSelectTitle,
      searchFields,
    } = prepareData;

    // =========== Filters ===========

    const onClearSearch = () => loadAuctionOptions();
    
    const onSearchTextChanged = (searchParams) => setSearchParams(searchParams);

    const setAuctionAndLoadLanes = (auctionId, auctionName) => {
      // update new auction
      setAuction({ value: auctionId, text: auctionName });

      // load lanes for new auction
      loadLaneOptions(auctionId, auctionName);

      // reset message type selection
      const { value, text } = allMessageType;
      onMessageTypeChanged(value, text);
    };

    // ******************************************
    // AUCTIONS
    // ******************************************

    const loadAuctionOptions = () => {
      
      setIsLoadingAuctions(true);
      
      // pull in the first auction from list or one that is set
      const auctionId = auction?.value || sortedActiveAuctions[0].value;
      const auctionName = getOptionTextByValue(sortedActiveAuctions, auctionId);
      
      setAuctionAndLoadLanes(auctionId, auctionName);

      setIsLoadingAuctions(false);
    };

    const onAuctionChanged = (value = null, text = null) => {
      if (!value || !text) return;

      const auctionName = text;
      const auctionId = value;

      setLane({key: null, value: null, text: null})
      setAuctionAndLoadLanes(auctionId, auctionName);
    };

    // ******************************************
    // LANES
    // ******************************************

    const loadLaneOptions = (auctionId, auctionName) => {
      setIsLoadingLanes(true);

      loadAuctionLaneOptions(auctionId).then((laneOptions) => {
        // pull in the appropriate lane to display
        
        const selectedLane = getLoadedLaneWithAllOption(laneOptions, lane, {
          auctionId,
          auctionName,
        });

        // update new lane
        setLane(selectedLane);

        // console.log('loadLaneOptions', { auctionName, selectedLane, laneOptions, lane,})
        // set heading text
        onAuctionLaneChange(auctionName, selectedLane?.text);

        setIsLoadingLanes(false);
      });
    };

    const onLaneChanged = (value = null, text = null) => {
      if (!value || !text) return;

      // update new lane
      setLane({ key: value, value, text });

      // set heading text
      onAuctionLaneChange(auction?.text, text);
    };

    // ******************************************
    // MESSAGE TYPES
    // ******************************************

    const loadMessageTypeOptions = () => {

      // pull in the first message type from list or one that is set
      const messageTypeId = messageType?.value || messageTypes[0].value;
      const messageTypeName = getOptionTextByValue(messageTypes, messageTypeId);

      // update new message type
      setMessageType({ key: messageTypeId, value: messageTypeId, text: messageTypeName });

      setIsLoadingMessageTypes(false);
    };

    const onMessageTypeChanged = (value = null, text = null) => {
      if (!value || !text) return;

      // update new message type
      setMessageType({ key: value, value, text });
    };

    useEffect(() => {
      if (sortedActiveAuctions.length) {
        loadAuctionOptions();
      }
    }, [sortedActiveAuctions, AuctionID]);

    useEffect(() => {
      // load auction data
      getSortedActiveAuctions();

      // load message types
      loadMessageTypeOptions();
    }, []);

    useEffect(() => {
      setHeaderText('');
    }, []);


    return (
      <SimulcastTable
        bodySchema={bodySchema}
        debugging={debugging}
        headerSchema={headerSchema}
        initialMediaState={null}
        initialSearchParams={searchParams}
        isLoading={isLoading}
        key={tableKey}
        methods={methods}
        onSearchTextChanged={onSearchTextChanged}
        onClearSearch={onClearSearch}
        pageSelectTitle={pageSelectTitle}
        rowData={data}
        searchFields={searchFields}
        state={{
          auctionName: auction.value,
          laneName: lane.value,
          messageType: messageType.value,
        }}
        totalItems={totalItems}
        exportButton={{
          visible: data.length > 0,
          fileName: `AuctionTranscriptReport-${auction?.text}`,
          sheetName: 'Transcript Report',
          showLocation: ['top'],
        }}
      >
        {/* <FlexCard horizontalAlignment="center" style={{ marginTop: '10px' }} >
          <FlexCard> */}
            <Selection
              onChange={onAuctionChanged}
              title="Auction"
              options={sortedActiveAuctions}
              defaultValue={auction?.value}
              // style={{ marginRight: '50px' }}
              isLoading={isLoadingAuctions}
            />
            <Selection
              onChange={onLaneChanged}
              title="Lane"
              options={laneOptions}
              defaultValue={lane?.value}
              // style={{ marginRight: '50px' }}
              isLoading={isLoadingLanes}
            />
            <Selection
              onChange={onMessageTypeChanged}
              title="Message Type"
              options={messageTypes}
              defaultValue={messageType?.value}
              // style={{ marginRight: '50px' }}
              isLoading={isLoadingMessageTypes}
            />
            {/* <ReactHTMLTableToExcel
              className="btn btn-info"
              table="table-to-xls"
              filename={`AuctionTranscriptReport-${auction?.text}`}
              sheet={`Transcript Report`}
              buttonText="Export to Excel"
            /> */}
          {/* </FlexCard> */}
          {/* <FlexCard
            style={{
              justifyItems: 'flex-end',
              alignSelf: 'center',
            }}
          >
            <ReactHTMLTableToExcel
              className="btn btn-info"
              table="table-to-xls"
              filename={`AuctionTranscriptReport-${auction?.text}`}
              sheet={`Transcript Report`}
              buttonText="Export to Excel"
            />
          </FlexCard> */}
        {/* </FlexCard> */}
      </SimulcastTable>
    );
  },
);

export default AuctionTranscriptReportHome;

// utility

export const getLoadedLaneWithAllOption = (
  lanes = [],
  state,//FIXME: REMOVE, NOT DOING ANYTHING
  { auctionId = null, auctionName = null } = {},
) => {
  if (!lanes.length) {
    console.error(
      `It appears the ${auctionName} auction does not contain any lanes`,
      { lanes, auctionId, auctionName },
    );
    return {};
  }

  const ALL_LANE_OPTION_VALUE = 0;
  const ONLY_LANE_OPTION_VALUE = 1;
  const hasSingleLaneExcludeAllLaneOption = lanes.length < 3;
  const hasNoLanes = lanes.length === 1;
  const value = hasSingleLaneExcludeAllLaneOption
  ? ONLY_LANE_OPTION_VALUE
  : ALL_LANE_OPTION_VALUE;
  
  // const id = state?.value || lanes[value].value;
  // console.log('brian', {optionValueToUse: hasNoLanes ? ALL_LANE_OPTION_VALUE : value, value, state, lanes, auctionId, auctionName})
  const id = lanes[hasNoLanes ? ALL_LANE_OPTION_VALUE : value].value;
  
  
  const name = getOptionTextByValue(lanes, id);
  // console.log('lane options', {hasSingleLaneExcludeAllLaneOption, lanes, value, id, name})
    // console.log('getLoadedLaneWithAllOption', { hasSingleLaneExcludeAllLaneOption, state, lanes, value, id, name })

  return { key: id, value: id, text: name };
};

